import React, { ReactNode, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  GetLabsAvailabilityApiService,
  GetLabsAvailabilityDay,
  GetLabsAvailabilitySlot,
} from "../../../services/GetLabsAvailabilityApiService";
import PhlebotomyCheckbox from "../components/PhlebotomyCheckbox";
import {
  AddressSummaryLabel,
  AddressSummaryText,
  DayChip,
  DayChipDate,
  DayChipMeta,
  DayChipWeekday,
  DayStrip,
  EditDetailsButton,
  GetLabsScheduleContext,
  SlotContext,
  TimeSlotChip,
  TimeSlotGrid,
} from "./MobilePhlebotomyGetLabsSchedulePage.styles";
import PhlebotomyAddressAutocomplete, {
  PhlebotomyStructuredAddress,
} from "./PhlebotomyAddressAutocomplete";
import {
  getLocalDateString,
  US_STATE_ABBREVIATION_FORMAT_REGEX,
  US_ZIP_CODE_REGEX,
} from "./phlebotomyFormUtils";

export interface GetLabsAvailabilitySelection {
  address: PhlebotomyStructuredAddress;
  hasKit: boolean;
  slot: GetLabsAvailabilitySlot;
  timezone?: string;
}

export type GetLabsAvailabilityUnavailableReason =
  | "availability_error"
  | "needs_kit"
  | "no_slots"
  | "unserved";

interface GetLabsAvailabilitySelectorProps {
  step: "location" | "times";
  address: PhlebotomyStructuredAddress | null;
  addressLine2: string;
  hasKit: boolean;
  alreadyScheduledActions: ReactNode;
  onAddressChange: (address: PhlebotomyStructuredAddress | null) => void;
  onAddressLine2Change: (addressLine2: string) => void;
  onAlreadyScheduledChange?: (alreadyScheduled: boolean) => void;
  onKitStatusChange: (hasKit: boolean) => void;
  onSelectionChange: (selection: GetLabsAvailabilitySelection | null) => void;
  onStepChange: (step: "location" | "times") => void;
  onUnavailable: (input: {
    address: PhlebotomyStructuredAddress;
    hasKit: boolean;
    reason: GetLabsAvailabilityUnavailableReason;
  }) => void;
}

const AVAILABILITY_DAYS = 7;

interface ManualAddressFields {
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
}

const formatDayParts = (dateIso: string, locale?: string) => {
  const [year, month, day] = dateIso.split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  return {
    weekday: date.toLocaleDateString(locale, { weekday: "short" }),
    dayNumber: date.toLocaleDateString(locale, { day: "numeric" }),
    month: date.toLocaleDateString(locale, { month: "short" }),
  };
};

const formatTime = (iso: string, locale?: string, timezone?: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    ...(timezone ? { timeZone: timezone } : {}),
  });
};

const formatTimeZoneLabel = (timeZone?: string, locale?: string) => {
  if (!timeZone) return undefined;
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: "longGeneric",
    })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
  } catch {
    return timeZone;
  }
};

const GetLabsAvailabilitySelector: React.FC<
  GetLabsAvailabilitySelectorProps
> = ({
  address,
  addressLine2,
  alreadyScheduledActions,
  hasKit,
  onAddressChange,
  onAddressLine2Change,
  onAlreadyScheduledChange,
  onKitStatusChange,
  onSelectionChange,
  onStepChange,
  onUnavailable,
  step,
}) => {
  const { t, i18n } = useTranslation();
  const [addressInput, setAddressInput] = useState("");
  const [useManualAddress, setUseManualAddress] = useState(false);
  const [manualAddressAttempted, setManualAddressAttempted] = useState(false);
  const [manualAddressFields, setManualAddressFields] =
    useState<ManualAddressFields>({
      addressLine1: address?.addressLine1 || "",
      city: address?.city || "",
      state: address?.state || "",
      postalCode: address?.postalCode || "",
    });
  const [alreadyScheduled, setAlreadyScheduled] = useState(false);
  const [resolvingAddress, setResolvingAddress] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [days, setDays] = useState<GetLabsAvailabilityDay[]>([]);
  const [timezone, setTimezone] = useState<string>();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlotKey, setSelectedSlotKey] = useState("");

  useEffect(() => {
    if (address?.formattedAddress) {
      setAddressInput(address.formattedAddress);
    }
  }, [address?.formattedAddress]);
  const manualAddress = useMemo(() => {
    const addressLine1 = manualAddressFields.addressLine1.trim();
    const city = manualAddressFields.city.trim();
    const state = manualAddressFields.state.trim().toUpperCase();
    const postalCode = manualAddressFields.postalCode.trim();
    if (
      !addressLine1 ||
      !city ||
      !US_STATE_ABBREVIATION_FORMAT_REGEX.test(state) ||
      !US_ZIP_CODE_REGEX.test(postalCode)
    ) {
      return null;
    }
    return {
      placeId: "manual",
      formattedAddress: [addressLine1, city, state, postalCode].join(", "),
      addressLine1,
      addressLine2,
      city,
      state,
      postalCode,
    } satisfies PhlebotomyStructuredAddress;
  }, [addressLine2, manualAddressFields]);
  const activeAddress = useManualAddress ? manualAddress : address;

  const daysWithSlots = useMemo(
    () => days.filter((day) => day.slots.some((slot) => slot.key)),
    [days],
  );
  const dayStrip = useMemo(() => {
    const requestedWindow = Array.from(
      { length: AVAILABILITY_DAYS },
      (_, offset) => getLocalDateString(offset + 1),
    );
    return Array.from(
      new Set([...requestedWindow, ...days.map((day) => day.date)]),
    )
      .sort()
      .map((date) => ({
        date,
        bookable: daysWithSlots.some((day) => day.date === date),
      }));
  }, [days, daysWithSlots]);
  const selectedDaySlots = useMemo(
    () =>
      (
        daysWithSlots.find((day) => day.date === selectedDate)?.slots || []
      ).filter((slot) => slot.key),
    [daysWithSlots, selectedDate],
  );
  const timezoneLabel = useMemo(
    () => formatTimeZoneLabel(timezone, i18n.language),
    [i18n.language, timezone],
  );
  const addressSummary = [
    activeAddress?.addressLine1,
    addressLine2.trim(),
    activeAddress &&
      [activeAddress.city, activeAddress.state].filter(Boolean).join(", "),
    activeAddress?.postalCode,
  ]
    .filter(Boolean)
    .join(" · ");
  let locationSubmitLabel = t(
    "phlebotomySearch.mobileSchedule.continueToPreferences",
  );
  if (hasKit)
    locationSubmitLabel = t("phlebotomySearch.mobileSchedule.findTimes");
  if (loadingSlots)
    locationSubmitLabel = t("phlebotomySearch.mobileSchedule.findingTimes");

  const clearAvailability = () => {
    setDays([]);
    setTimezone(undefined);
    setSelectedDate("");
    setSelectedSlotKey("");
    onSelectionChange(null);
  };

  const handleAddressChange = (
    nextAddress: PhlebotomyStructuredAddress | null,
  ) => {
    setAddressError(null);
    clearAvailability();
    onAddressChange(nextAddress);
  };

  const handleManualAddressToggle = () => {
    clearAvailability();
    setAddressError(null);
    setManualAddressAttempted(false);
    setAddressInput("");
    if (!useManualAddress) {
      setManualAddressFields({
        addressLine1: address?.addressLine1 || "",
        city: address?.city || "",
        state: address?.state || "",
        postalCode: address?.postalCode || "",
      });
      onAddressChange(null);
      onAddressLine2Change(addressLine2);
      setUseManualAddress(true);
      return;
    }
    setManualAddressFields({
      addressLine1: "",
      city: "",
      state: "",
      postalCode: "",
    });
    setUseManualAddress(false);
    onAddressChange(null);
    onAddressLine2Change(addressLine2);
  };

  const handleManualAddressFieldChange =
    (field: keyof ManualAddressFields) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setManualAddressFields((current) => ({
        ...current,
        [field]: event.target.value,
      }));
      setAddressError(null);
      clearAvailability();
    };

  const handleAddressLine2Change = (nextAddressLine2: string) => {
    clearAvailability();
    onAddressLine2Change(nextAddressLine2);
  };

  const continueToCareTeam = (
    reason: GetLabsAvailabilityUnavailableReason,
    fallbackAddress = activeAddress,
  ) => {
    if (fallbackAddress)
      onUnavailable({
        address: { ...fallbackAddress, addressLine2 },
        hasKit,
        reason,
      });
  };

  const loadAvailability = async () => {
    setManualAddressAttempted(true);
    if (!activeAddress) {
      setAddressError(
        useManualAddress
          ? t("phlebotomySearch.mobileSchedule.errors.required")
          : t("phlebotomySearch.mobileSchedule.errors.selectAddress"),
      );
      return;
    }
    if (useManualAddress) {
      onAddressChange(activeAddress);
    }
    if (!hasKit) {
      continueToCareTeam("needs_kit");
      return;
    }

    setLoadingSlots(true);
    setAddressError(null);
    clearAvailability();
    try {
      const result = await GetLabsAvailabilityApiService.getAvailability({
        street: activeAddress.addressLine1,
        unit: addressLine2.trim() || undefined,
        city: activeAddress.city,
        state: activeAddress.state,
        zip: activeAddress.postalCode,
        fromDate: getLocalDateString(1),
        days: AVAILABILITY_DAYS,
      });
      const firstBookableDate = result.days.find((day) =>
        day.slots.some((slot) => slot.key),
      )?.date;
      if (!result.serviceable) {
        continueToCareTeam("unserved");
        return;
      }
      if (!firstBookableDate) {
        continueToCareTeam("no_slots");
        return;
      }
      setDays(result.days);
      setTimezone(result.timezone || undefined);
      setSelectedDate(firstBookableDate);
      onStepChange("times");
    } catch {
      continueToCareTeam("availability_error");
    } finally {
      setLoadingSlots(false);
    }
  };

  if (step === "location") {
    return (
      <Box
        component="section"
        aria-label={t("phlebotomySearch.mobileSchedule.addressSection")}
      >
        <Typography
          component="h2"
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 1.5 }}
        >
          {t("phlebotomySearch.mobileSchedule.addressSection")}
        </Typography>
        {useManualAddress ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              fullWidth
              required
              autoComplete="address-line1"
              label={t("phlebotomySearch.mobileSchedule.addressLine1")}
              value={manualAddressFields.addressLine1}
              disabled={loadingSlots}
              error={
                manualAddressAttempted &&
                !manualAddressFields.addressLine1.trim()
              }
              helperText={
                manualAddressAttempted &&
                !manualAddressFields.addressLine1.trim()
                  ? t("phlebotomySearch.mobileSchedule.errors.required")
                  : undefined
              }
              onChange={handleManualAddressFieldChange("addressLine1")}
            />
            <TextField
              fullWidth
              required
              autoComplete="address-level2"
              label={t("phlebotomySearch.mobileSchedule.city")}
              value={manualAddressFields.city}
              disabled={loadingSlots}
              error={manualAddressAttempted && !manualAddressFields.city.trim()}
              helperText={
                manualAddressAttempted && !manualAddressFields.city.trim()
                  ? t("phlebotomySearch.mobileSchedule.errors.required")
                  : undefined
              }
              onChange={handleManualAddressFieldChange("city")}
            />
            <TextField
              fullWidth
              required
              autoComplete="address-level1"
              inputProps={{ maxLength: 2 }}
              label={t("phlebotomySearch.mobileSchedule.state")}
              value={manualAddressFields.state}
              disabled={loadingSlots}
              error={
                manualAddressAttempted &&
                !US_STATE_ABBREVIATION_FORMAT_REGEX.test(
                  manualAddressFields.state.trim(),
                )
              }
              helperText={
                manualAddressAttempted &&
                !US_STATE_ABBREVIATION_FORMAT_REGEX.test(
                  manualAddressFields.state.trim(),
                )
                  ? t("phlebotomySearch.mobileSchedule.errors.state")
                  : undefined
              }
              onChange={handleManualAddressFieldChange("state")}
            />
            <TextField
              fullWidth
              required
              autoComplete="postal-code"
              label={t("phlebotomySearch.mobileSchedule.postalCode")}
              value={manualAddressFields.postalCode}
              disabled={loadingSlots}
              error={
                manualAddressAttempted &&
                !US_ZIP_CODE_REGEX.test(manualAddressFields.postalCode.trim())
              }
              helperText={
                manualAddressAttempted &&
                !US_ZIP_CODE_REGEX.test(manualAddressFields.postalCode.trim())
                  ? t("phlebotomySearch.mobileSchedule.errors.zip")
                  : undefined
              }
              onChange={handleManualAddressFieldChange("postalCode")}
            />
          </Box>
        ) : (
          <PhlebotomyAddressAutocomplete
            label={t("phlebotomySearch.mobileSchedule.addressSearchLabel")}
            inputValue={addressInput}
            value={address}
            disabled={loadingSlots}
            error={Boolean(addressError)}
            helperText={
              addressError ||
              t("phlebotomySearch.mobileSchedule.addressSearchHelp")
            }
            onInputValueChange={setAddressInput}
            onChange={handleAddressChange}
            onBusyChange={setResolvingAddress}
          />
        )}
        {(useManualAddress || address) && (
          <TextField
            fullWidth
            autoComplete="address-line2"
            label={t("phlebotomySearch.mobileSchedule.addressLine2")}
            value={addressLine2}
            disabled={loadingSlots}
            sx={{ mt: 1.5 }}
            onChange={(event) => {
              handleAddressLine2Change(event.target.value);
            }}
          />
        )}
        <Button
          type="button"
          disabled={loadingSlots || resolvingAddress}
          sx={{ alignSelf: "flex-start", mt: 1 }}
          onClick={handleManualAddressToggle}
        >
          {t(
            useManualAddress
              ? "phlebotomySearch.mobileSchedule.searchAddressInstead"
              : "phlebotomySearch.mobileSchedule.enterAddressManually",
          )}
        </Button>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t("phlebotomySearch.mobileSchedule.beforeContinuing")}
          </Typography>
          <FormControlLabel
            sx={{
              alignItems: "flex-start",
              m: 0,
              minWidth: 0,
              "& .MuiCheckbox-root": { p: 0.5, mr: 1 },
              "& .MuiFormControlLabel-label": { pt: 0.5 },
            }}
            control={
              <PhlebotomyCheckbox
                checked={hasKit}
                disabled={loadingSlots}
                onChange={(event) => {
                  clearAvailability();
                  onKitStatusChange(event.target.checked);
                }}
              />
            }
            label={t("phlebotomySearch.mobileSchedule.hasKitConfirmation")}
          />
          <FormControlLabel
            sx={{
              alignItems: "flex-start",
              m: 0,
              minWidth: 0,
              "& .MuiCheckbox-root": { p: 0.5, mr: 1 },
              "& .MuiFormControlLabel-label": { pt: 0.5 },
            }}
            control={
              <PhlebotomyCheckbox
                checked={alreadyScheduled}
                disabled={loadingSlots}
                onChange={(event) => {
                  setAlreadyScheduled(event.target.checked);
                  if (event.target.checked) clearAvailability();
                  onAlreadyScheduledChange?.(event.target.checked);
                }}
              />
            }
            label={t(
              "phlebotomySearch.mobileSchedule.alreadyScheduledConfirmation",
            )}
          />
          {alreadyScheduled ? (
            alreadyScheduledActions
          ) : (
            <Button
              variant="unity-primary"
              disabled={
                loadingSlots ||
                resolvingAddress ||
                (!useManualAddress && !address)
              }
              onClick={() => loadAvailability().catch(() => undefined)}
            >
              {locationSubmitLabel}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-label={t("phlebotomySearch.mobileSchedule.pickTime")}
    >
      <GetLabsScheduleContext>
        <Box sx={{ px: 1.75, py: 1.5 }}>
          <AddressSummaryLabel>
            {t("phlebotomySearch.mobileSchedule.drawAddress")}
          </AddressSummaryLabel>
          <AddressSummaryText>{addressSummary}</AddressSummaryText>
          <EditDetailsButton
            type="button"
            onClick={() => {
              clearAvailability();
              onStepChange("location");
            }}
          >
            {t("phlebotomySearch.mobileSchedule.editDetails")}
          </EditDetailsButton>
        </Box>
        <Alert
          severity="success"
          sx={{
            borderRadius: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiAlert-message": { py: 0.25 },
          }}
        >
          {t("phlebotomySearch.mobileSchedule.coverageServed", {
            zip: activeAddress?.postalCode || "",
          })}
        </Alert>
      </GetLabsScheduleContext>
      <Typography
        component="h2"
        variant="subtitle1"
        fontWeight={700}
        sx={{ mb: 1 }}
      >
        {t("phlebotomySearch.mobileSchedule.pickDay")}
      </Typography>
      <DayStrip
        role="listbox"
        aria-label={t("phlebotomySearch.mobileSchedule.dayStripLabel")}
      >
        {dayStrip.map((day) => {
          const parts = formatDayParts(day.date, i18n.language);
          const selected = day.bookable && day.date === selectedDate;
          return (
            <DayChip
              key={day.date}
              type="button"
              selected={selected}
              aria-selected={selected}
              disabled={!day.bookable}
              onClick={() => {
                setSelectedDate(day.date);
                setSelectedSlotKey("");
                onSelectionChange(null);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                <DayChipWeekday>{parts.weekday}</DayChipWeekday>
                <DayChipDate>{parts.dayNumber}</DayChipDate>
                <DayChipMeta>
                  {day.bookable
                    ? parts.month
                    : t("phlebotomySearch.mobileSchedule.dayUnavailable")}
                </DayChipMeta>
              </Box>
            </DayChip>
          );
        })}
      </DayStrip>
      <Typography
        component="h2"
        variant="subtitle1"
        fontWeight={700}
        sx={{ mb: 1 }}
      >
        {t("phlebotomySearch.mobileSchedule.pickTime")}
      </Typography>
      {timezoneLabel && (
        <SlotContext>
          {t("phlebotomySearch.mobileSchedule.timesShownIn", {
            timezone: timezoneLabel,
          })}
        </SlotContext>
      )}
      <TimeSlotGrid
        role="listbox"
        aria-label={t("phlebotomySearch.mobileSchedule.timeGridLabel")}
      >
        {selectedDaySlots.map((slot) => {
          const selected = selectedSlotKey === slot.key;
          return (
            <TimeSlotChip
              key={slot.key}
              type="button"
              selected={selected}
              aria-selected={selected}
              onClick={() => {
                setSelectedSlotKey(slot.key as string);
                if (activeAddress)
                  onSelectionChange({
                    address: { ...activeAddress, addressLine2 },
                    hasKit,
                    slot,
                    timezone,
                  });
              }}
            >
              {`${formatTime(slot.start, i18n.language, timezone)} – ${formatTime(slot.end, i18n.language, timezone)}`}
            </TimeSlotChip>
          );
        })}
      </TimeSlotGrid>
      {!selectedSlotKey && (
        <FormHelperText>
          {t("phlebotomySearch.mobileSchedule.errors.slot")}
        </FormHelperText>
      )}
    </Box>
  );
};

export default GetLabsAvailabilitySelector;
